/*
 *  -----------------------------------------------------------------------
 *    <copyright file="study-summary.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { useTranslation } from 'hooks';
import { OrganisationProgramFields, StudyFields } from 'queries/generated/fragments.generated';
import { GET_STUDY_LOGO } from 'queries/studies';
import { getLabels } from 'utils/convertor';
import { GetMimeType } from 'views/components/core/edit-logo/image-service';
import Loading from 'views/components/core/loading';

import 'views/components/admin/studies/admin-study.scss';

const StudySummary = ({ study, programs }: { study: StudyFields; programs: OrganisationProgramFields[] }) => {
    const { t } = useTranslation();
    const [loadLogo, { data }] = useLazyQuery(GET_STUDY_LOGO);
    const [imgSrc, setImgSrc] = useState(null);

    useEffect(() => {
        if (study) loadLogo({ variables: { studyId: study.id } });
    }, [study]);

    useEffect(() => {
        if (data?.study?.logo) {
            const { logo } = data.study;
            const mimeType = GetMimeType(logo);
            if (mimeType) setImgSrc(`data:${mimeType};base64,${logo}`);
            else setImgSrc(logo);
        } else setImgSrc(null);
    }, [data]);

    if (!study) return <Loading />;

    return (
        <div className="study-settings-wrapper">
            <div className="d-flex between horizontal-start">
                <div className="data-wrap">
                    <div className="row">
                        <span className="title">
                            <strong>{t('cm:study_num')}:</strong>
                        </span>
                        <span className="value">{study.studyNumber}</span>
                    </div>
                    <div className="row">
                        <span className="title">
                            <strong>{t('common:shortName')}:</strong>
                        </span>
                        <span className="value">{study.shortName}</span>
                    </div>
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:program')}:</strong>
                        </span>
                        <span className="value">{getLabels(study.programsIds, programs, 'name')}</span>
                    </div>
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:sponsor')}:</strong>
                        </span>
                        <span className="value">{study.sponsor}</span>
                    </div>
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:title')}:</strong>
                        </span>
                        <span className="value">{study.title}</span>
                    </div>
                </div>
                {data && data.study && data.study.logo && (
                    <div className="image-wrap">
                        <img alt="" src={imgSrc} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default StudySummary;
